import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Alert } from '@/components/ui/Alert';
import { Modal } from '@/components/ui/Modal';
import { Progress } from '@/v5/components/ui/progress';
import * as leadsSvc from '@/services/leads.service';
import * as intakeSvc from '@/services/leadIntake.service';
import * as adminSvc from '@/services/admin.service';
import * as patientsSvc from '@/services/patients.service';
import { checkCredentials } from '@/services/patients.service';
import * as scannerSvc from '@/services/scannerBridge.service';
import * as patientFilesSvc from '@/services/patient-files.service';
import { listDoctors, formatDoctorName } from '@/services/doctors.service';
import { listCoordinators, formatCoordinatorName } from '@/services/coordinators.service';
import { listNurses, formatNurseName } from '@/services/nurses.service';
import { phoneLengthError } from '@/components/medical/PhoneInput';
import { todayIsoDate } from '@/components/ui/CalendarDatePicker';
import { CameraCaptureModal } from '@/components/medical/CameraCaptureModal';
import { toast } from '@/store/toastStore';
import { Loader2, X, FileText, Users } from 'lucide-react';
import { formatPhone } from '@/lib/utils';
import { normalizeDialCode } from '@/lib/phone';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import { DetailRightPanel } from '@/components/layout/DetailRightPanel';
import { DetailProfile } from '@/components/layout/DetailProfile';
import { usePatientDetail } from '@/hooks/usePatientDetail';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { prependLeadDetails } from './leadDetailsSummary';
import type { LeadSourceCategory } from '@/services/admin.service';
import type { ApiError } from '@/types/api.types';
import { isApiError } from '@/types/api.types';
import { getNewPatientForm } from '@/services/newPatientForm.service';
import { ConfiguredCard } from './newPatientForm/ConfiguredCard';
import { FIELD_RENDERERS, type NewLeadFieldContext, type ScanPhase } from './newPatientForm/fieldRenderers';
import {
  FORM_RETRY_LABEL,
  FormSettingsGate,
  PATIENT_DETAILS_FAILED_MESSAGE,
  type FormSettingsStatus,
} from './newPatientForm/FormSettingsGate';
import {
  LAST_NAME_ERROR_FIELD,
  buildErrorList,
  firstErrorAnchor,
  requiredErrors,
  toFieldErrors,
  type ExtraErrorField,
} from './newPatientForm/errorList';
import { selectedOptionHints } from './newPatientForm/fieldHints';
import { WARNING_TOAST_TITLE, warningMessages } from './newPatientForm/warningMessages';
import { buildIntakePayload } from './newPatientForm/buildIntakePayload';
import { leadPrefilledKeys } from './newPatientForm/leadPrefill';
import {
  applyVisibleDefaults,
  birthDateError,
  isShown,
  resolveFormMode,
  startingCountry,
  type FormState,
} from './newPatientForm/newPatientFormRules';


/** Inputs with their own error that are not configurable fields. */
const EXTRA_ERROR_FIELDS: ExtraErrorField[] = [
  LAST_NAME_ERROR_FIELD,
  {
    key: 'referralPatient',
    label: 'Referring Patient',
    anchorId: 'field-referralPatient',
    afterKey: 'referral_select_1_anchor',
  },
];

/** Scroll a field's wrapper into view and focus its first focusable control. */
function focusFieldById(anchorId: string) {
  const el = document.getElementById(anchorId);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const focusable = el.querySelector<HTMLElement>(
    'input,select,textarea,button,[tabindex]:not([tabindex="-1"])',
  );
  focusable?.focus({ preventScroll: true });
}

type DuplicateConflict = {
  planId: number;
  planTypeName: string | null;
  name: string | null;
  date: string | null;
};

function hasReadableFrontExtraction(extraction: scannerSvc.IdExtractionResult): boolean {
  return Boolean(
    extraction.firstName ||
    extraction.lastName ||
    extraction.birthDate ||
    extraction.address ||
    extraction.idNumber,
  );
}

function base64ToImageFile(base64: string, filename: string, mimeType: string = 'image/jpeg'): File {
  const cleaned = base64.includes(',') ? base64.split(',')[1] : base64;
  const binary = atob(cleaned);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mimeType });
}

/**
 * Prefill rule (2026-09-16): a matched record's value wins, but a BLANK one
 * never wipes what is already on the form. The lead often carries a DOB or an
 * address the shared/local record lacks, and overwriting it with '' loses data
 * the staff member just typed. Returns a state updater, so it composes with the
 * existing setters. Legacy zero-dates count as blank.
 */
function fillOrKeep(incoming: unknown) {
  return (current: string) => {
    const next = String(incoming ?? '').trim();
    return next !== '' && !next.startsWith('0000-00-00') ? next : current;
  };
}

function fillOrKeepGender(incoming: string | null | undefined) {
  return (current: string | null) => intakeSvc.normalizeGenderOption(incoming) ?? current;
}

/** Comparison forms for the two contact fields a shared-patient link rests on. */
const contactKey = (value: string) => value.trim().toLowerCase();
const digitsOnly = (value: string) => value.replace(/\D/g, '');

function parsePositiveInt(value: string | null): number | null {
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** Payload handed to `onSuccess` so an embedding host can seed a patient chip
 *  without a second round trip. `POST /leads/intake` itself returns only
 *  `{ patientId, planId }`, so the demographics come from local form state. */
export interface CreatedIntakePatient {
  patientId: number;
  planId: number;
  firstName: string;
  lastName: string;
  birthDate: string | null;
}

export interface NewLeadPageProps {
  /**
   * Render for a modal host instead of the `/leads/new` route: drops this
   * component's own page header, saving overlay and page-height shell, and
   * takes its mode from props instead of the URL. Every prop below is ignored
   * unless this is set, so the route keeps its current behaviour exactly.
   */
  embedded?: boolean;
  /** Convert-a-lead mode (route equivalent: `?leadId=`). */
  leadId?: number | null;
  /** Add-Plan mode for an existing patient (route equivalent: `?patientId=`). */
  patientId?: number | null;
  /** Pre-select the required Plan Setup → Facility field. */
  defaultFacilityId?: number | null;
  /** Called instead of navigating to the new plan. */
  onSuccess?: (created: CreatedIntakePatient) => void;
  /** Called instead of navigating away when Cancel is pressed. */
  onCancel?: () => void;
  /** Lets the host render the saving overlay on its own chrome. */
  onSavingChange?: (saving: boolean) => void;
}

export default function NewLeadPage({
  embedded = false,
  leadId: leadIdProp = null,
  patientId: patientIdProp = null,
  defaultFacilityId = null,
  onSuccess,
  onCancel,
  onSavingChange,
}: NewLeadPageProps = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // When embedded, mode comes from props ONLY. Reading the URL here would let a
  // host route's own query string leak in — a `?patientId=` on the host page
  // would silently flip this form into Add-Plan mode and hide the entire
  // Patient Information card.
  const leadId = embedded
    ? leadIdProp
    : parsePositiveInt(searchParams.get('leadId') ?? searchParams.get('from'));
  const initialPatientId = embedded ? patientIdProp : parsePositiveInt(searchParams.get('patientId'));
  const isAddPlan = initialPatientId !== null;

  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [maidenName, setMaidenName] = useState('');
  const [gender, setGender] = useState<string | null>(null);
  const [birthDate, setBirthDate] = useState('');

  const [cellPhone, setCellPhone] = useState('');
  /** The cell's dial code ('44'), never a countryID. */
  const [cellCountryCode, setCellCountryCode] = useState<string>('1');
  const [smsOptIn, setSmsOptIn] = useState(true);
  const [marketingSms, setMarketingSms] = useState(true);
  const [email, setEmail] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(true);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState<string>('');
  const [zip, setZip] = useState('');
  const [addressCountry, setAddressCountry] = useState<string>('US');

  const [referralCategoryId, setReferralCategoryId] = useState<number | null>(null);
  const [referralSourceId, setReferralSourceId] = useState<number | null>(null);
  const [referralPatient, setReferralPatient] = useState<import('@/types/patient.types').PatientSummary | null>(null);
  const [doctorId, setDoctorId] = useState<number | null>(null);
  const [coordinatorId, setCoordinatorId] = useState<number | null>(null);
  const [nurseId, setNurseId] = useState<number | null>(null);
  const [facilityId, setFacilityId] = useState<number | null>(defaultFacilityId);
  const [planTypeId, setPlanTypeId] = useState<number | null>(null);

  const [timeframe, setTimeframe] = useState<number | null>(null);
  const [commitmentLevel, setCommitmentLevel] = useState<number | null>(null);
  const [revenueOpportunity, setRevenueOpportunity] = useState('');
  const [notes, setNotes] = useState('');

  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(initialPatientId);
  // A master patient record chosen from the exact email/phone master match. The
  // new patient created on submit is linked to it (server re-checks the match).
  const [selectedMaster, setSelectedMaster] = useState<leadsSvc.MasterPatientMatch | null>(null);
  // The email/phone the form carried when that master was selected — see the
  // unlink effect below. Null whenever no link is pending.
  const masterContactRef = useRef<{ email: string; phone: string } | null>(null);
  const [allowDuplicatePlanType, setAllowDuplicatePlanType] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [duplicateConflict, setDuplicateConflict] = useState<DuplicateConflict | null>(null);
  const [validating, setValidating] = useState(false);

  const [expandPatientPanel, setExpandPatientPanel] = useState(0);
  const [scannerAvailable, setScannerAvailable] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanPhase, setScanPhase] = useState<ScanPhase>('idle');
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanConfidence, setScanConfidence] = useState<{ overall: 'high' | 'medium' | 'low'; notes: string | null } | null>(null);
  const [extractingUpload, setExtractingUpload] = useState(false);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [capturedPreview, setCapturedPreview] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const appliedLeadIdRef = useRef<number | null>(null);
  const appliedPatientIdRef = useRef<number | null>(null);
  const appliedScanRef = useRef(false);
  // Catalog keys the lead prefill filled; a hidden one is still carried into
  // the new patient and plan on a lead conversion (KTD7).
  const [leadKeys, setLeadKeys] = useState<Set<string>>(() => new Set());
  // The lead / patient whose prefill has been applied (or skipped). Visible
  // defaults wait for these so they only fill what the prefill left empty.
  const [leadSettledId, setLeadSettledId] = useState<number | null>(null);
  const [patientSettledId, setPatientSettledId] = useState<number | null>(null);
  const defaultsSeededRef = useRef(false);
  // The country the lead or patient supplied, and whether staff have picked
  // one themselves; both outrank the configured default and account country.
  const countryPrefillRef = useRef<string | null>(null);
  const countryTouchedRef = useRef(false);

  // The account's New Patient form settings: which fields show, in what
  // order, with which labels, hints and defaults. No fallback layout renders
  // without them, and Save stays disabled until they load.
  const settingsQuery = useQuery({
    queryKey: ['new-patient-form'],
    queryFn: getNewPatientForm,
    staleTime: 0,
  });
  // Only settings fetched on this visit: a copy cached by an earlier visit
  // can predate an admin's change, and the defaults are seeded once. A failed
  // first fetch shows the failure rather than that copy; once this visit's
  // fetch has succeeded, a failed refetch (on window focus) keeps the form.
  const freshSettings = settingsQuery.isFetchedAfterMount && !settingsQuery.isError;
  const [settingsLoadedThisVisit, setSettingsLoadedThisVisit] = useState(false);
  useEffect(() => {
    if (freshSettings && settingsQuery.data) setSettingsLoadedThisVisit(true);
  }, [freshSettings, settingsQuery.data]);
  const formSettings = freshSettings || settingsLoadedThisVisit ? settingsQuery.data : undefined;
  const formFields = useMemo(() => formSettings?.fields ?? [], [formSettings]);
  const settingsStatus: FormSettingsStatus = formSettings
    ? 'ready'
    : settingsQuery.isError
      ? 'error'
      : 'loading';

  const leadSourceCategoriesQuery = useQuery<LeadSourceCategory[]>({
    queryKey: ['admin', 'lead-source-categories'],
    queryFn: adminSvc.listLeadSourceCategories,
    staleTime: 5 * 60_000,
    // Fetched on every visit: a Lead Source default the cached list does not
    // know yet would otherwise be skipped as belonging to no category.
    refetchOnMount: 'always',
  });
  const leadSourceCategories = useMemo(() => leadSourceCategoriesQuery.data ?? [], [leadSourceCategoriesQuery.data]);
  // Which category each Lead Source belongs to, so a Lead Source default is
  // seeded only under its own category.
  const sourceCategoryOf = useMemo(() => {
    const byId = new Map<number, number>();
    for (const category of leadSourceCategories) {
      for (const source of category.leadSources) byId.set(source.referralLevel2ID, category.referralLevel1ID);
    }
    return (sourceId: number): number | null => byId.get(sourceId) ?? null;
  }, [leadSourceCategories]);
  const { roles } = useRoleTitles();
  const { detail: patientDetail, loading: patientDetailLoading } = usePatientDetail(
    isAddPlan && initialPatientId ? String(initialPatientId) : null,
  );
  const { data: doctors = [] } = useQuery({
    queryKey: ['doctors', 'active'],
    queryFn: () => listDoctors({ active: true }),
  });
  const { data: coordinators = [] } = useQuery({
    queryKey: ['coordinators', 'active'],
    queryFn: () => listCoordinators({ active: true }),
  });
  const { data: nurses = [] } = useQuery({
    queryKey: ['nurses', 'active'],
    queryFn: () => listNurses({ active: true }),
  });
  const { data: facilities = [] } = useQuery({
    queryKey: ['admin', 'facilities'],
    queryFn: adminSvc.listFacilities,
  });
  const { data: planTypes = [] } = useQuery({
    queryKey: ['admin', 'plan-types'],
    queryFn: adminSvc.listPlanTypes,
  });
  const { data: timeframes = [] } = useQuery({
    queryKey: ['admin', 'timeframes'],
    queryFn: adminSvc.listTimeframes,
  });
  const { data: countryCodes = [] } = useQuery({
    queryKey: ['country-codes'],
    queryFn: patientsSvc.listCountryCodes,
  });
  const { data: accountCountryAbbr } = useQuery({
    queryKey: ['account-country-abbr'],
    queryFn: patientsSvc.getAccountCountryAbbr,
  });
  const { data: leadPrefill, isError: leadPrefillFailed } = useQuery({
    queryKey: ['lead-intake', 'lead-prefill', leadId],
    queryFn: () => leadsSvc.getLead(leadId!),
    enabled: leadId !== null,
  });
  const {
    data: patientPrefill,
    isError: patientPrefillFailed,
    isFetching: patientPrefillFetching,
    refetch: refetchPatientPrefill,
  } = useQuery({
    queryKey: ['lead-intake', 'patient-prefill', selectedPatientId],
    queryFn: () => intakeSvc.getIntakePatient(selectedPatientId!),
    enabled: selectedPatientId !== null,
  });
  const trimmedEmail = email.trim();
  const trimmedPhone = cellPhone.replace(/\D/g, '');
  const hasNameSearch = firstName.trim().length > 0 && lastName.trim().length > 0;
  const hasEmailSearch = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  const hasPhoneSearch = trimmedPhone.length >= 10;
  const matchEnabled = hasNameSearch || hasEmailSearch || hasPhoneSearch;

  const searchEmail = hasEmailSearch ? trimmedEmail : '';
  const searchPhone = hasPhoneSearch ? trimmedPhone : '';

  const { data: rawSimilarNames = [], isFetching: similarNamesLoading, isError: similarNamesError } = useQuery({
    queryKey: ['lead-intake', 'similar-names', firstName.trim(), lastName.trim(), searchEmail, searchPhone],
    queryFn: () => leadsSvc.searchSimilarNames(
      firstName.trim(),
      lastName.trim(),
      searchEmail || undefined,
      searchPhone || undefined,
    ),
    enabled: matchEnabled,
  });

  const similarNames = useMemo(() => {
    return [...rawSimilarNames].sort((a, b) => {
      const aReasons = a.matchReasons ?? [];
      const bReasons = b.matchReasons ?? [];
      const aHasContactMatch = aReasons.includes('phone') || aReasons.includes('email') ? 1 : 0;
      const bHasContactMatch = bReasons.includes('phone') || bReasons.includes('email') ? 1 : 0;
      if (bHasContactMatch !== aHasContactMatch) return bHasContactMatch - aHasContactMatch;
      return bReasons.length - aReasons.length;
    });
  }, [rawSimilarNames]);
  const { data: patientPlans = [] } = useQuery({
    queryKey: ['lead-intake', 'patient-plans', selectedPatientId],
    queryFn: () => intakeSvc.listPatientPlans(selectedPatientId!),
    enabled: selectedPatientId !== null,
  });

  // Master-patient pass (2026-09-11): EXACT email/phone only, against the master
  // list for this account's master account. The in-account check above keeps
  // precedence — this list is offered only while it finds nothing.
  const masterMatchEnabled = !isAddPlan && selectedPatientId === null && (hasEmailSearch || hasPhoneSearch);
  const { data: masterMatches = [], isFetching: masterMatchesLoading } = useQuery({
    queryKey: ['lead-intake', 'master-matches', searchEmail, searchPhone],
    queryFn: () => leadsSvc.searchMasterMatches(searchEmail || undefined, searchPhone || undefined),
    enabled: masterMatchEnabled,
  });
  // An in-account candidate matching on EMAIL or PHONE is the same person
  // already here — the in-account flow wins and the master list stays hidden.
  // Name-only similarity does not hide it: the master uniqueness rule would
  // otherwise block the save with no way to link.
  const internalEmailMatch = similarNames.some((s) => (s.matchReasons ?? []).includes('email'));
  const internalPhoneMatch = similarNames.some((s) => (s.matchReasons ?? []).includes('phone'));
  const internalContactMatch = internalEmailMatch || internalPhoneMatch;

  const masterEmailMatch = masterMatches.some((m) => m.matchReasons.includes('email'));
  const masterPhoneMatch = masterMatches.some((m) => m.matchReasons.includes('phone'));
  // A contact field the master matches that NO in-account candidate matches.
  const masterOnlyEmail = masterEmailMatch && !internalEmailMatch;
  const masterOnlyPhone = masterPhoneMatch && !internalPhoneMatch;
  const masterAddsMatch = masterOnlyEmail || masterOnlyPhone;
  // Split match: one contact field points at a patient in THIS account and the
  // other at a master record — two different people, or one person recorded two
  // ways. Both lists stay visible and staff must resolve it; hiding the master
  // list here would leave a save the server blocks with no way to link.
  const splitContactMatch = internalContactMatch && masterAddsMatch;

  const contactLabel = (hasEmail: boolean, hasPhone: boolean) =>
    hasEmail && hasPhone ? 'email and phone' : hasEmail ? 'email' : 'phone';
  // Who the record is shared with: the corporation THIS account belongs to. The
  // sibling account the person is a patient at is never disclosed, so the copy
  // says "another <corp> account" and stops there.
  const sharedOrgName = masterMatches.find((m) => m.masterAccountName)?.masterAccountName ?? null;
  const sharedFromLabel = sharedOrgName ? `another ${sharedOrgName} account` : 'another account';
  const splitNote = splitContactMatch
    ? `The ${contactLabel(internalEmailMatch, internalPhoneMatch)} matches a patient in this account, ` +
      `and the ${contactLabel(masterOnlyEmail, masterOnlyPhone)} matches a shared patient from ${sharedFromLabel}.`
    : '';

  const showMasterMatches =
    masterMatchEnabled &&
    !similarNamesLoading &&
    (!internalContactMatch || masterAddsMatch) &&
    masterMatches.length > 0 &&
    selectedMaster === null;
  // Contact preferences belong to the existing record (in-account patient or
  // master) and are never editable from the intake form.
  const prefsLocked = selectedPatientId !== null || selectedMaster !== null;

  useEffect(() => {
    // An in-account selection always wins over a pending master link.
    if (selectedPatientId !== null) {
      setSelectedMaster(null);
      masterContactRef.current = null;
    }
  }, [selectedPatientId]);

  const applyMasterMatch = useCallback((m: leadsSvc.MasterPatientMatch) => {
    if (m.linkedPatientId) {
      // Already a patient in this account → the existing in-account flow.
      setSelectedPatientId(m.linkedPatientId);
      return;
    }
    setSelectedMaster(m);
    setFirstName(fillOrKeep(m.firstName));
    setMiddleName(fillOrKeep(m.middleName));
    setLastName(fillOrKeep(m.lastName));
    setMaidenName(fillOrKeep(m.maidenName));
    setGender(fillOrKeepGender(m.gender));
    setBirthDate(fillOrKeep(m.birthDate));
    if (m.cellCountryCode) setCellCountryCode(normalizeDialCode(m.cellCountryCode));
    setAddress(fillOrKeep(m.address));
    setCity(fillOrKeep(m.city));
    setState(fillOrKeep(m.state));
    setZip(fillOrKeep(m.zip && String(m.zip) !== '0' ? m.zip : ''));
    setEmailOptIn(m.emailOptIn);
    setSmsOptIn(m.smsOptIn);
    setMarketingEmails(!m.emailUnsubscribe);
    setMarketingSms(!m.smsUnsubscribe);

    // The email/phone the link rests on. Editing either one drops the link
    // (the effect below) — a changed contact detail means a different identity,
    // and the server would refuse to link it anyway (`no_exact_match`).
    const nextEmail = fillOrKeep(m.email)(email);
    const nextPhone = fillOrKeep(m.formCellPhone)(cellPhone);
    setEmail(nextEmail);
    setCellPhone(nextPhone);
    masterContactRef.current = { email: contactKey(nextEmail), phone: digitsOnly(nextPhone) };
  }, [cellPhone, email]);

  // Drops the pending shared-patient link as soon as the email or cell phone is
  // edited away from what the match supplied. The match panel then reappears (it
  // only shows while nothing is selected), so staff can re-select the shared
  // patient or finish the change and save a standalone record.
  useEffect(() => {
    const applied = masterContactRef.current;
    if (selectedMaster === null || applied === null) return;
    const emailChanged = contactKey(email) !== applied.email;
    const phoneChanged = digitsOnly(cellPhone) !== applied.phone;
    if (!emailChanged && !phoneChanged) return;
    masterContactRef.current = null;
    setSelectedMaster(null);
    toast.info(
      'Shared patient unlinked',
      `The ${emailChanged && phoneChanged ? 'email and cell phone' : emailChanged ? 'email' : 'cell phone'} no longer ` +
        'matches the shared patient, so the new patient will be created on its own.',
    );
  }, [cellPhone, email, selectedMaster]);

  const referralCategoryOptions = useMemo(
    () => [
      { value: 0, label: 'Select One...' },
      ...leadSourceCategories
        .filter((c) => c.active === true || c.active === '1' || c.referralLevel1ID === referralCategoryId)
        .map((c) => ({ value: c.referralLevel1ID, label: c.name })),
    ],
    [leadSourceCategories, referralCategoryId],
  );

  const referralSourceOptions = useMemo(() => {
    const cat = leadSourceCategories.find((c) => c.referralLevel1ID === referralCategoryId);
    if (!cat) return [{ value: 0, label: 'Select One...' }];
    return [
      { value: 0, label: 'Select One...' },
      ...cat.leadSources
        .filter((s) => s.active === true || s.active === '1' || s.referralLevel2ID === referralSourceId)
        .map((s) => ({ value: s.referralLevel2ID, label: s.name })),
    ];
  }, [leadSourceCategories, referralCategoryId, referralSourceId]);

  // Decided by the API (it matches the category name, as legacy does) rather than
  // guessed from the id ordering here. Kept identical to PlansTab on purpose — the two
  // screens collect the same referral and must agree on which category it is.
  const establishedPatientReferrerId = useMemo(() => {
    const match = leadSourceCategories.find((c) => c.isPatientReferralCategory);
    return match ? match.referralLevel1ID : null;
  }, [leadSourceCategories]);

  const isEstablishedPatientSource = referralCategoryId !== null && referralCategoryId === establishedPatientReferrerId;

  const doctorLabel = roles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'Doctor';
  const coordLabel = roles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';
  const nurseLabel = roles.find((r) => r.roleType === 'Nurse')?.roleTitle || 'Nurse';

  // Which settings apply (KTD4): converting a lead, adding a plan from the
  // patient, or a new patient (including one picked in the match panel).
  const formMode = resolveFormMode({ leadId, patientRoute: isAddPlan });

  // Labels the form shows when a field has no custom label: the account's
  // role titles, and the Canadian address captions.
  const captions = useMemo<Record<string, string>>(() => ({
    doctor: doctorLabel,
    coordinator: coordLabel,
    nurse: nurseLabel,
    state: addressCountry === 'CA' ? 'Province' : 'State',
    zip: addressCountry === 'CA' ? 'Postal Code' : 'ZIP',
  }), [addressCountry, coordLabel, doctorLabel, nurseLabel]);

  const formState: FormState = {
    firstName,
    middleName,
    lastName,
    maidenName,
    gender,
    birthDate,
    idScanCaptured: capturedFile !== null,
    cellPhone,
    cellCountryCode,
    smsOptIn,
    marketingSms,
    email,
    emailOptIn,
    marketingEmails,
    address,
    city,
    state,
    zip,
    addressCountry,
    referralCategoryId,
    referralSourceId,
    referralPatient: referralPatient
      ? { patientId: referralPatient.patientId, firstName: referralPatient.firstName, lastName: referralPatient.lastName }
      : null,
    doctorId,
    coordinatorId,
    nurseId,
    facilityId,
    planTypeId,
    timeframe,
    commitmentLevel,
    revenueOpportunity,
    notes,
  };

  // Seed each visible field's default once settings and any lead / patient
  // prefill have settled, filling only values still empty (R8, KTD7).
  const leadSettled = leadId === null || leadSettledId === leadId || leadPrefillFailed;
  const patientSettled = initialPatientId === null || patientSettledId === initialPatientId || patientPrefillFailed;
  // Save waits for the existing patient's details (Add Plan, or a match-panel
  // pick): saving with the form's starting values would overwrite the
  // patient's own. A failed load keeps Save off and offers a retry.
  const selectedPatientReady = selectedPatientId === null || patientSettledId === selectedPatientId;
  const selectedPatientFailed = selectedPatientId !== null && patientPrefillFailed && !selectedPatientReady;
  // The Lead Source default also waits for this visit's categories, which say
  // whether it belongs under the category on the form. A failed fetch seeds
  // the rest of the defaults and leaves the source empty.
  const leadSourcesSettled = leadSourceCategoriesQuery.isFetchedAfterMount;
  useEffect(() => {
    if (defaultsSeededRef.current || !formSettings || !leadSettled || !patientSettled || !leadSourcesSettled) return;
    defaultsSeededRef.current = true;
    const seeded = applyVisibleDefaults(formState, formSettings.fields, formMode, sourceCategoryOf);
    if (seeded === formState) return;
    if (seeded.gender !== gender) setGender(seeded.gender);
    if (seeded.state !== state) setState(seeded.state);
    if (seeded.addressCountry !== addressCountry) setAddressCountry(seeded.addressCountry);
    if (seeded.planTypeId !== planTypeId) setPlanTypeId(seeded.planTypeId);
    if (seeded.facilityId !== facilityId) setFacilityId(seeded.facilityId);
    if (seeded.referralCategoryId !== referralCategoryId) setReferralCategoryId(seeded.referralCategoryId);
    if (seeded.referralSourceId !== referralSourceId) setReferralSourceId(seeded.referralSourceId);
    if (seeded.doctorId !== doctorId) setDoctorId(seeded.doctorId);
    if (seeded.coordinatorId !== coordinatorId) setCoordinatorId(seeded.coordinatorId);
    if (seeded.nurseId !== nurseId) setNurseId(seeded.nurseId);
    if (seeded.commitmentLevel !== commitmentLevel) setCommitmentLevel(seeded.commitmentLevel);
    if (seeded.timeframe !== timeframe) setTimeframe(seeded.timeframe);
    // formState is rebuilt every render; the ref makes this run once.
  }, [formSettings, leadSettled, patientSettled, leadSourcesSettled]);

  const optionHints = useMemo(
    () => selectedOptionHints(formSettings?.optionHints, formState),
    // Only the chosen ids matter.
    [formSettings, doctorId, facilityId, referralCategoryId, referralSourceId],
  );

  const doctorOptions = doctors.map((d) => ({
    value: d.doctorID,
    label: formatDoctorName(d) || `Doctor #${d.doctorID}`,
  }));

  const coordinatorOptions = coordinators.map((c) => ({
    value: c.coordID,
    label: formatCoordinatorName(c) || `Coordinator #${c.coordID}`,
  }));

  const nurseOptions = nurses.map((n) => ({
    value: n.nurseID,
    label: formatNurseName(n) || `Nurse #${n.nurseID}`,
  }));

  const facilityOptions = facilities
    .filter((f) => f.active)
    .map((f) => ({
      value: f.facilityId,
      label: f.facilityName,
    }));

  const planTypeOptions = planTypes
    .filter((planType) => planType.active)
    .map((planType) => ({
      value: planType.planTypeId,
      label: planType.name,
    }));

  const timeframeOptions = timeframes.map((tf) => ({
    value: tf.timeframeId,
    label: tf.name,
  }));

  const duplicatePlan = useMemo(() => {
    if (selectedPatientId === null || planTypeId === null) {
      return null;
    }

    return patientPlans.find((plan) => plan.planTypeId === planTypeId) ?? null;
  }, [patientPlans, planTypeId, selectedPatientId]);

  const scanProgress = useMemo(() => {
    switch (scanPhase) {
      case 'scanning':
        return {
          value: 35,
          title: 'Scanning ID',
          description: 'Capturing images from the selected scanner.',
        };
      case 'uploading':
        return {
          value: 68,
          title: 'Uploading Images',
          description: 'Preparing the scanned images for extraction.',
        };
      case 'extracting':
        return {
          value: 92,
          title: 'Extracting Fields',
          description: 'Analyzing the ID and pre-filling the form.',
        };
      default:
        return null;
    }
  }, [scanPhase]);

  useEffect(() => {
    if (!leadPrefill || appliedLeadIdRef.current === leadPrefill.leadId) {
      return;
    }
    if (appliedScanRef.current) {
      // The scanned ID wins over the lead; defaults need not wait any longer.
      setLeadSettledId(leadPrefill.leadId);
      return;
    }

    appliedLeadIdRef.current = leadPrefill.leadId;
    setFirstName(leadPrefill.firstName ?? '');
    setMiddleName(leadPrefill.middleName ?? '');
    setLastName(leadPrefill.lastName ?? '');
    setMaidenName(leadPrefill.maidenName ?? '');
    setGender(intakeSvc.normalizeGenderOption(leadPrefill.gender));
    setBirthDate(leadPrefill.birthDate ?? '');
    setCellPhone(leadPrefill.cellPhone || leadPrefill.phone || '');
    setEmail(leadPrefill.email ?? '');
    setAddress(leadPrefill.address ?? '');
    setCity(leadPrefill.city ?? '');
    setState(leadPrefill.state ?? '');
    setZip(leadPrefill.zip && String(leadPrefill.zip) !== '0' ? String(leadPrefill.zip) : '');
    // The lead's own country. Without one, the starting-country effect below
    // applies the configured default or the account's country.
    if (leadPrefill.country && (leadPrefill.country === 'US' || leadPrefill.country === 'CA')) {
      countryPrefillRef.current = leadPrefill.country;
      setAddressCountry(leadPrefill.country);
    }
    setSmsOptIn(Boolean(leadPrefill.smsOptIn));
    setEmailOptIn(Boolean(leadPrefill.emailOptIn));
    setMarketingSms(leadPrefill.marketingSms !== undefined ? Boolean(leadPrefill.marketingSms) : true);
    setMarketingEmails(leadPrefill.marketingEmail !== undefined ? Boolean(leadPrefill.marketingEmail) : true);
    console.log('[lead-convert] lead prefill applied to intake form', {
      leadId: leadPrefill.leadId,
      smsOptIn: leadPrefill.smsOptIn,
      emailOptIn: leadPrefill.emailOptIn,
      marketingSms: leadPrefill.marketingSms,
      marketingEmail: leadPrefill.marketingEmail,
      planTypeId: leadPrefill.planTypeId,
      planTypeName: leadPrefill.planTypeName,
      facilityId: leadPrefill.facilityId,
      leadSourceId: leadPrefill.leadSourceId,
      referralSourceId: leadPrefill.referralSourceId,
    });
    setReferralCategoryId(leadPrefill.leadSourceId ?? null);
    setReferralSourceId(leadPrefill.referralSourceId ?? null);
    setDoctorId(leadPrefill.doctorId ?? null);
    setCoordinatorId(leadPrefill.coordinatorId ?? null);
    // No nurse to prefill: a lead (contacts row) only carries doctorID/coordID.
    setFacilityId(leadPrefill.facilityId ?? null);
    setPlanTypeId(leadPrefill.planTypeId ?? null);
    const timeframeNumeric = leadPrefill.timeframe != null && leadPrefill.timeframe !== ''
      ? Number(leadPrefill.timeframe)
      : NaN;
    const leadTimeframe = Number.isFinite(timeframeNumeric) && timeframeNumeric > 0 ? timeframeNumeric : null;
    setTimeframe(leadTimeframe);
    const commitmentNumeric = leadPrefill.ballparkOpinion != null && leadPrefill.ballparkOpinion !== ''
      ? Number(leadPrefill.ballparkOpinion)
      : NaN;
    const leadCommitment = Number.isFinite(commitmentNumeric) && commitmentNumeric > 0 ? commitmentNumeric : null;
    setCommitmentLevel(leadCommitment);
    setRevenueOpportunity(leadPrefill.ballparkGiven ?? '');
    // Carry the lead's header details (name/email/address/DOB/source/custom form
    // answers) into the intake notes alongside the message body — the message
    // alone loses everything shown at the top of LeadDetailModal.
    const leadNotes = prependLeadDetails(leadPrefill, leadPrefill.notes || leadPrefill.message || '');
    setNotes(leadNotes);

    // What the lead itself supplied (not the account-country fallback), so a
    // field hidden on this form still carries the lead's value (R9).
    setLeadKeys(leadPrefilledKeys({
      firstName: leadPrefill.firstName ?? '',
      middleName: leadPrefill.middleName ?? '',
      lastName: leadPrefill.lastName ?? '',
      maidenName: leadPrefill.maidenName ?? '',
      gender: intakeSvc.normalizeGenderOption(leadPrefill.gender),
      birthDate: leadPrefill.birthDate ?? '',
      cellPhone: leadPrefill.cellPhone || leadPrefill.phone || '',
      email: leadPrefill.email ?? '',
      address: leadPrefill.address ?? '',
      city: leadPrefill.city ?? '',
      state: leadPrefill.state ?? '',
      zip: leadPrefill.zip && String(leadPrefill.zip) !== '0' ? String(leadPrefill.zip) : '',
      addressCountry: leadPrefill.country === 'US' || leadPrefill.country === 'CA' ? leadPrefill.country : '',
      referralCategoryId: leadPrefill.leadSourceId ?? null,
      referralSourceId: leadPrefill.referralSourceId ?? null,
      doctorId: leadPrefill.doctorId ?? null,
      coordinatorId: leadPrefill.coordinatorId ?? null,
      facilityId: leadPrefill.facilityId ?? null,
      planTypeId: leadPrefill.planTypeId ?? null,
      timeframe: leadTimeframe,
      commitmentLevel: leadCommitment,
      revenueOpportunity: leadPrefill.ballparkGiven ?? '',
      notes: leadNotes,
    }));
    setLeadSettledId(leadPrefill.leadId);
  }, [leadPrefill]);

  // The starting address country: the lead's or patient's own, else Country's
  // configured default, else the account's country, else US. It waits for the
  // initial lead / patient prefill, re-applies as settings and the account
  // country arrive, and stops for good once staff pick a country themselves.
  const countryField = formFields.find((f) => f.key === 'country');
  useEffect(() => {
    if (countryTouchedRef.current || !leadSettled || !patientSettled) return;
    setAddressCountry(startingCountry({
      prefill: countryPrefillRef.current,
      countryField,
      mode: formMode,
      accountCountry: accountCountryAbbr,
    }));
  }, [accountCountryAbbr, countryField, formMode, leadSettled, patientSettled]);

  useEffect(() => {
    if (!patientPrefill || appliedPatientIdRef.current === patientPrefill.patientId) {
      return;
    }
    if (appliedScanRef.current && appliedPatientIdRef.current === null) {
      setPatientSettledId(patientPrefill.patientId);
      return;
    }

    appliedPatientIdRef.current = patientPrefill.patientId;
    setPatientSettledId(patientPrefill.patientId);
    setFirstName(fillOrKeep(patientPrefill.firstName));
    setMiddleName(fillOrKeep(patientPrefill.middleName));
    setLastName(fillOrKeep(patientPrefill.lastName));
    setMaidenName(fillOrKeep(patientPrefill.maidenName));
    setGender(fillOrKeepGender(patientPrefill.gender));
    setBirthDate(fillOrKeep(patientPrefill.birthDate));
    setCellPhone(fillOrKeep(patientPrefill.cellPhone));
    if (patientPrefill.cellCountryCode) setCellCountryCode(normalizeDialCode(patientPrefill.cellCountryCode));
    setEmail(fillOrKeep(patientPrefill.email));
    setAddress(fillOrKeep(patientPrefill.address));
    setCity(fillOrKeep(patientPrefill.city));
    setState(fillOrKeep(patientPrefill.state));
    setZip(fillOrKeep(patientPrefill.zip && String(patientPrefill.zip) !== '0' ? patientPrefill.zip : ''));
    // The patient's own address country, so the State/ZIP inputs and the
    // country sent on save match the patient rather than the account default.
    if (patientPrefill.countryAbbr === 'US' || patientPrefill.countryAbbr === 'CA') {
      countryPrefillRef.current = patientPrefill.countryAbbr;
      setAddressCountry(patientPrefill.countryAbbr);
    }
    setFacilityId(patientPrefill.facilityId ?? facilityId);
    setEmailOptIn(Boolean(patientPrefill.emailOptIn));
    setSmsOptIn(Boolean(patientPrefill.smsOptIn));
    // Marketing flags are stored inverted (unsubscribe = true means opted out).
    setMarketingEmails(!patientPrefill.emailUnsubscribe);
    setMarketingSms(!patientPrefill.smsUnsubscribe);
  }, [facilityId, patientPrefill]);

  useEffect(() => {
    setAllowDuplicatePlanType(false);
    setDuplicateConflict(null);
  }, [planTypeId, selectedPatientId]);

  useEffect(() => {
    if (!isEstablishedPatientSource) {
      setReferralPatient(null);
    }
  }, [isEstablishedPatientSource]);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 3;

    const tryDetect = async () => {
      if (cancelled) return;
      attempts++;
      const ok = await scannerSvc.pingScannerHost();
      if (cancelled) return;
      if (ok) {
        setScannerAvailable(true);
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(tryDetect, 2000);
      }
    };

    tryDetect();
    return () => { cancelled = true; };
  }, []);

  const applyScanExtraction = useCallback((extraction: scannerSvc.IdExtractionResult) => {
    const hasReadableFront = hasReadableFrontExtraction(extraction);
    if (extraction.firstName) setFirstName(extraction.firstName);
    if (extraction.middleName) setMiddleName(extraction.middleName);
    if (extraction.lastName) setLastName(extraction.lastName);
    if (extraction.gender) setGender(extraction.gender);
    if (extraction.birthDate) setBirthDate(extraction.birthDate);
    if (extraction.address) setAddress(extraction.address);
    if (extraction.city) setCity(extraction.city);
    if (extraction.state) setState(extraction.state);
    if (extraction.zip) setZip(String(extraction.zip));
    setScanConfidence({
      ...extraction.confidence,
      notes: extraction.confidence.notes ?? (
        hasReadableFront
          ? 'Fields have been pre-filled from the scanned ID. Please review and correct any errors before submitting.'
          : 'No readable front of the ID was detected. Please rescan or upload clearer images.'
      ),
    });
    appliedScanRef.current = hasReadableFront;
  }, []);

  const handleCapturedFile = useCallback(async (file: File) => {
    setCapturedFile(file);
    setCapturedPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    });
    setScanError(null);
    setScanConfidence(null);
    if (!file.type.startsWith('image/')) return;
    setExtractingUpload(true);
    setScanPhase('extracting');
    try {
      const result = await scannerSvc.extractFromUploadedImages(file, null);
      applyScanExtraction(result.extraction);
      if (result.correctedFrontImage) {
        const format = result.correctedFrontFormat || 'jpeg';
        const baseName = file.name.replace(/\.[^./\\]+$/, '');
        const ext = format === 'jpeg' ? 'jpg' : format;
        const correctedFile = base64ToImageFile(result.correctedFrontImage, `${baseName}.${ext}`, `image/${format}`);
        setCapturedFile(correctedFile);
        setCapturedPreview((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(correctedFile);
        });
      }
    } catch (err) {
      setScanError((err as Error).message ?? 'Extraction failed.');
    } finally {
      setExtractingUpload(false);
      setScanPhase('idle');
    }
  }, [applyScanExtraction]);

  const handleScanId = useCallback(async () => {
    setScanning(true);
    setScanPhase('scanning');
    setScanError(null);
    setScanConfidence(null);
    try {
      const scanResult = await scannerSvc.requestScan();
      const file = base64ToImageFile(scanResult.frontImage, `scan_${Date.now()}.jpg`);
      await handleCapturedFile(file);
    } catch (err) {
      setScanError((err as Error).message ?? 'Scan failed.');
    } finally {
      setScanning(false);
      setScanPhase('idle');
    }
  }, [handleCapturedFile]);

  const handleFileInputChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) {
      await handleCapturedFile(file);
    }
  }, [handleCapturedFile]);

  const handleRemoveCapturedFile = useCallback(() => {
    setCapturedFile(null);
    setCapturedPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setScanConfidence(null);
    appliedScanRef.current = false;
  }, []);

  const handleReExtract = useCallback(() => {
    if (capturedFile) {
      void handleCapturedFile(capturedFile);
    }
  }, [capturedFile, handleCapturedFile]);

  useEffect(() => {
    return () => {
      if (capturedPreview) URL.revokeObjectURL(capturedPreview);
    };
  }, [capturedPreview]);

  const createMutation = useMutation({
    mutationFn: intakeSvc.submitLeadIntake,
    onSuccess: async ({ patientId, planId, masterPatientId, masterLink, warnings }) => {
      console.log('[lead-convert] conversion succeeded', { leadId: leadId ?? undefined, patientId, planId, masterPatientId, masterLink, warnings });
      if (masterLink === 'linked') {
        toast.success('Linked to shared patient', `Patient #${patientId} now shares record #${masterPatientId}.`);
      } else if (masterLink) {
        toast.warning('Shared patient not linked', intakeSvc.describeMasterLinkStatus(masterLink));
      }
      // Steps after the save that did not finish (KTD9). The toast above
      // already explains a refused shared-patient link, so that one is not
      // repeated here.
      const followUps = warningMessages(warnings, { masterLinkReported: Boolean(masterLink) });
      if (followUps.length > 0) {
        toast.warning(WARNING_TOAST_TITLE, followUps.join(' '));
      }
      setSubmissionError(null);
      setDuplicateConflict(null);
      if (capturedFile) {
        try {
          await patientFilesSvc.uploadFile(patientId, capturedFile);
        } catch (err) {
          toast.error('Intake saved, but file upload failed: ' + ((err as Error).message ?? 'unknown error'));
        }
      }
      // Mark the patient's plans list stale so the Plans tab we're about to land
      // on refetches and includes the newly created plan (default staleTime is
      // 30s, so a recently-cached list would otherwise omit it).
      queryClient.invalidateQueries({ queryKey: ['patient-plans', patientId] });

      if (embedded) {
        // Must be the LAST statement: react-query awaits a promise returned
        // from onSuccess, so the uploadFile above runs while the host still
        // shows its saving overlay. Handing control back any earlier lets the
        // host unmount us mid-upload and the scanned ID never attaches.
        onSuccess?.({
          patientId,
          planId,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          birthDate: birthDate && !birthDate.startsWith('0000') ? birthDate : null,
        });
        return;
      }

      navigate(`/patients/${patientId}/plans/${planId}`);
    },
    onError: (error) => {
      console.error('[lead-convert] conversion failed', error);
      if (isApiError(error)) {
        const payload = error.response.data as ApiError;
        setSubmissionError(payload.error.message);

        // Required fields the save found empty (KTD8). This form's settings may
        // be stale (an admin changed them while it was open), so reload them:
        // a required field hidden here then appears, and the list below links
        // to it.
        if (payload.error.code === 'REQUIRED_FIELDS' || payload.error.code === 'VALIDATION_ERROR') {
          const details = (payload.error.details ?? {}) as { fields?: unknown };
          const fieldErrors = toFieldErrors(details.fields);
          if (payload.error.code === 'REQUIRED_FIELDS') {
            void settingsQuery.refetch();
          }
          if (Object.keys(fieldErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...fieldErrors }));
            setShowErrorModal(true);
          }
          return;
        }

        // The server re-checks email/phone uniqueness at save time (the pre-flight
        // check above can go stale). Map its per-field errors onto the same fields so
        // the failure lands on the input rather than only in the banner.
        if ((payload.error.code === 'DUPLICATE_CREDENTIALS' || payload.error.code === 'MASTER_PATIENT_MATCH')
            && payload.error.details && typeof payload.error.details === 'object') {
          const details = payload.error.details as { errors?: Record<string, string> };
          if (details.errors) {
            const fieldErrors = toFieldErrors(details.errors);
            setErrors((prev) => ({ ...prev, ...fieldErrors }));
            setShowErrorModal(true);
          }
          return;
        }

        if (payload.error.code === 'DUPLICATE_PLAN_TYPE' && payload.error.details && typeof payload.error.details === 'object') {
          const details = payload.error.details as Record<string, unknown>;
          setDuplicateConflict({
            planId: Number(details.planId),
            planTypeName: typeof details.planTypeName === 'string' ? details.planTypeName : null,
            name: typeof details.name === 'string' ? details.name : null,
            date: typeof details.date === 'string' ? details.date : null,
          });
        }

        return;
      }

      setSubmissionError((error as Error)?.message ?? 'Failed to create the plan.');
    },
  });

  function validate(): Record<string, string> {
    // Required fields come from the account's settings for this mode (R11).
    // Patient and contact fields are not offered when adding a plan, so an
    // existing patient missing a DOB / email / phone is never blocked here.
    const nextErrors: Record<string, string> = requiredErrors(formFields, formMode, formState, captions, {
      // Same test the Lead Source renderer uses to decide whether it shows.
      referralSourcesOffered: referralSourceOptions.length > 1,
      isEstablishedPatientSource,
    });

    const cellField = formFields.find((f) => f.key === 'cell_phone');
    if (cellField && isShown(cellField, formMode) && cellPhone.trim()) {
      const phoneError = phoneLengthError(cellPhone);
      if (phoneError) nextErrors.cell_phone = phoneError;
    }
    const birthField = formFields.find((f) => f.key === 'birth_date');
    if (birthField && isShown(birthField, formMode)) {
      const dobError = birthDateError(birthDate, todayIsoDate());
      if (dobError) nextErrors.birth_date = dobError;
    }
    // Whether this category needs a referring patient is not knowable until the
    // categories arrive, and a category can already be set from a prefill by then. A
    // submit landing in that window would skip the check below and save a referral with
    // no referring patient on it.
    if (leadSourceCategories.length === 0) {
      nextErrors.referral_select_1_anchor = 'Lead sources are still loading — try again in a moment.';
    }
    if (isEstablishedPatientSource && !referralPatient) nextErrors.referralPatient = 'Referring patient is required';

    setErrors(nextErrors);
    return nextErrors;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    // Save is disabled until the form's settings load; Enter in a field can
    // still submit, so check here too.
    if (!formSettings || !selectedPatientReady) return;
    setSubmissionError(null);

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      // The modal covers the form, so surface the full list first; the field is
      // scrolled/focused when the modal closes (see the Modal's onClose below).
      setShowErrorModal(true);
      return;
    }

    if (duplicatePlan && !allowDuplicatePlanType) {
      setSubmissionError('This patient already has a plan with the selected plan type. Enable the duplicate override to continue.');
      return;
    }

    const cleanPhone = cellPhone.replace(/\D/g, '');
    const cleanEmail = email.trim();

    // Master-scope uniqueness: an exact master match must be LINKED, never
    // re-created as a standalone identity. The server enforces the same rule
    // (MASTER_PATIENT_MATCH); this just lands the message on the field first.
    // It fires on a split match too — there the in-account duplicate check below
    // would otherwise be the only thing to speak, and it never mentions the master.
    if (selectedPatientId === null && selectedMaster === null && masterMatches.length > 0) {
      const splitMessage = `${splitNote} Select one in the Patient Match panel, or correct the contact details.`;
      const nextErrors: Record<string, string> = {};
      if (masterEmailMatch) {
        nextErrors.email = splitContactMatch
          ? splitMessage
          : `A patient from ${sharedFromLabel} already uses this email. Two patients cannot share the same email or cell phone — select the shared patient in the Patient Match panel to link, or change the email.`;
      }
      if (masterPhoneMatch) {
        nextErrors.cell_phone = splitContactMatch
          ? splitMessage
          : `A patient from ${sharedFromLabel} already uses this cell phone. Two patients cannot share the same email or cell phone — select the shared patient in the Patient Match panel to link, or change the number.`;
      }
      setErrors((prev) => ({ ...prev, ...nextErrors }));
      setShowErrorModal(true);
      return;
    }

    if (selectedPatientId === null && (cleanEmail || cleanPhone)) {
      setValidating(true);
      try {
        const result = await checkCredentials(
          null,
          cleanEmail,
          cleanPhone,
          cellCountryCode,
        );
        if (!result.valid) {
          const nextErrors: Record<string, string> = {};
          if (result.errors.email) nextErrors.email = result.errors.email;
          if (result.errors.cellPhone) nextErrors.cell_phone = result.errors.cellPhone;
          setErrors((prev) => ({ ...prev, ...nextErrors }));
          // Email/phone fields may be scrolled off-screen — surface the same
          // modal so the uniqueness failure is never silent.
          setShowErrorModal(true);
          setValidating(false);
          return;
        }
      } catch {
        setSubmissionError('Unable to verify email/phone uniqueness. Please try again.');
        setValidating(false);
        return;
      }
      setValidating(false);
    }

    console.log('[lead-convert] converting lead → patient (submitting intake)', {
      leadId: leadId ?? undefined,
      isConversion: leadId != null,
      patientId: selectedPatientId ?? undefined,
      emailOptIn,
      smsOptIn,
      marketingEmails,
      marketingSms,
      emailUnsubscribe: !marketingEmails,
      smsUnsubscribe: !marketingSms,
      planTypeId,
      facilityId,
    });
    createMutation.mutate(buildIntakePayload({
      state: formState,
      fields: formSettings.fields,
      mode: formMode,
      leadPrefilledKeys: leadKeys,
      context: {
        leadId,
        patientId: selectedPatientId,
        masterPatientId: selectedMaster ? selectedMaster.masterPatientId : null,
        allowDuplicatePlanType,
        isEstablishedPatientSource,
        countryCodes,
      },
    }));
  }

  // Close the validation-error modal, then (after it unmounts and releases its
  // focus trap) scroll/focus the requested field. Passing no anchorId jumps to
  // the first errored field.
  function closeErrorModal(anchorId?: string) {
    setShowErrorModal(false);
    const target = anchorId ?? firstErrorAnchor(modalErrors);
    requestAnimationFrame(() => {
      if (target) focusFieldById(target);
    });
  }

  // Errors to list in the modal: every field with an error in render order,
  // labelled as the form labels it, then anything left over so nothing is
  // ever hidden.
  const modalErrors = buildErrorList({ fields: formFields, errors, captions, extras: EXTRA_ERROR_FIELDS });

  const showMatchPanel = !isAddPlan && (
    (similarNames.length > 0 && selectedPatientId === null) ||
    selectedPatientId !== null ||
    showMasterMatches ||
    (selectedMaster !== null && selectedPatientId === null)
  );

  const isSaving = validating || createMutation.isPending;

  // Embedded hosts render the saving veil over their own chrome (a modal panel
  // is viewport-bounded; our absolute overlay below spans the full ~2000px form
  // and would centre its spinner off-screen).
  useEffect(() => {
    onSavingChange?.(isSaving);
  }, [isSaving, onSavingChange]);

  // What the field markup (fieldRenderers.tsx) reads and writes.
  const fieldContext: NewLeadFieldContext = {
    firstName, setFirstName, middleName, setMiddleName, lastName, setLastName,
    maidenName, setMaidenName, gender, setGender, birthDate, setBirthDate,
    scan: {
      scannerAvailable,
      scanning,
      extractingUpload,
      scanPhase,
      onScan: handleScanId,
      onTakePhoto: () => setCameraOpen(true),
      fileInputRef,
      onFileChange: handleFileInputChange,
    },
    cellPhone, setCellPhone, cellCountryCode, setCellCountryCode, countryCodes, prefsLocked,
    smsOptIn, setSmsOptIn, marketingSms, setMarketingSms,
    email, setEmail, emailOptIn, setEmailOptIn, marketingEmails, setMarketingEmails,
    address, setAddress, city, setCity, state, setState, zip, setZip, addressCountry,
    setAddressCountry: (value: string) => {
      countryTouchedRef.current = true;
      setAddressCountry(value);
    },
    planTypeId, setPlanTypeId, planTypeOptions,
    facilityId, setFacilityId, facilityOptions,
    referralCategoryId, setReferralCategoryId, referralCategoryOptions,
    referralSourceId, setReferralSourceId, referralSourceOptions,
    isEstablishedPatientSource, referralPatient, setReferralPatient,
    doctorId, setDoctorId, doctorOptions,
    coordinatorId, setCoordinatorId, coordinatorOptions,
    nurseId, setNurseId, nurseOptions,
    revenueOpportunity, setRevenueOpportunity,
    commitmentLevel, setCommitmentLevel, commitmentOptions: intakeSvc.COMMITMENT_LEVEL_OPTIONS,
    timeframe, setTimeframe, timeframeOptions,
    notes, setNotes,
    errors,
  };

  const cardProps = {
    fields: formFields,
    mode: formMode,
    renderers: FIELD_RENDERERS,
    context: fieldContext,
    errors,
    captions,
    hintsEnabled: formSettings?.hintsEnabled ?? false,
    optionHints,
  };

  // Scan progress, confidence and the captured file, shown under the Patient
  // Information header.
  const scanPanel = (
    <>
      {scanError && (
        <Alert variant="error" dismissible onDismiss={() => setScanError(null)}>
          {scanError}
        </Alert>
      )}

      {scanProgress && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-slate-900">{scanProgress.title}</p>
              <p className="text-xs text-slate-500">{scanProgress.description}</p>
            </div>
            <span className="text-xs font-medium text-slate-500">{scanProgress.value}%</span>
          </div>
          <Progress value={scanProgress.value} className="h-2" />
        </div>
      )}

      {scanConfidence && scanConfidence.overall !== 'high' && (
        <Alert
          variant={scanConfidence.overall === 'medium' ? 'info' : 'warning'}
          title={`ID extraction confidence: ${scanConfidence.overall}`}
          dismissible
          onDismiss={() => setScanConfidence(null)}
        >
          {scanConfidence.notes}
        </Alert>
      )}

      {capturedFile && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center gap-3">
          <div className="h-14 w-14 flex-shrink-0 rounded border border-slate-200 bg-white overflow-hidden flex items-center justify-center">
            {capturedPreview ? (
              <img src={capturedPreview} alt="Captured" className="h-full w-full object-cover" />
            ) : (
              <FileText className="h-6 w-6 text-slate-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{capturedFile.name}</p>
            <p className="text-xs text-slate-500">
              {(capturedFile.size / 1024).toFixed(1)} KB
              {' • Will be saved to the patient\'s files after submit.'}
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {capturedFile.type.startsWith('image/') && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleReExtract}
                disabled={extractingUpload}
              >
                Re-extract
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconOnly
              onClick={handleRemoveCapturedFile}
              aria-label="Remove file"
              disabled={extractingUpload}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );

  const content = (
    <div
      className={
        embedded
          ? 'relative flex min-w-0 flex-col'
          // Below `md` the form stacks and its left column stops being a scroller, so
          // this wrapper has to take over as the one that scrolls. At `md` and above
          // it stays `overflow-hidden` exactly as before.
          : 'relative flex h-full flex-1 min-w-0 flex-col overflow-y-auto md:overflow-hidden'
      }
    >
      {isSaving && !embedded && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-sm font-medium text-slate-700">
              {validating ? 'Validating...' : isAddPlan ? 'Creating Plan...' : 'Creating Patient...'}
            </p>
          </div>
        </div>
      )}

      {/* Fixed header — the modal host supplies its own title */}
      {!embedded && (
      <div className="shrink-0 border-b border-slate-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <PageHeader
            variant="compact"
            title={isAddPlan ? 'Add New Plan' : 'Add New Patient'}
          />
          {isAddPlan && patientPrefill && (
            <button
              type="button"
              onClick={() => setExpandPatientPanel((v) => v + 1)}
              className="flex items-center gap-3 rounded-lg px-3 py-2 -mr-3 hover:bg-slate-50 transition-colors"
            >
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-900">
                  {[patientPrefill.firstName, patientPrefill.lastName].filter(Boolean).join(' ')}
                  <span className="ml-2 text-xs font-normal text-slate-400">
                    #{patientPrefill.displayId ?? patientPrefill.patientId}
                  </span>
                </p>
                <p className="text-xs text-slate-500">
                  {patientPrefill.birthDate && !patientPrefill.birthDate.startsWith('0000') && (() => {
                    const dob = new Date(patientPrefill.birthDate + 'T00:00:00');
                    if (isNaN(dob.getTime())) return null;
                    const age = Math.floor((Date.now() - dob.getTime()) / 31557600000);
                    const dateStr = dob.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    const genderStr = patientPrefill.gender
                      ? patientPrefill.gender.toUpperCase() === 'M' ? 'Male'
                        : patientPrefill.gender.toUpperCase() === 'F' ? 'Female'
                        : patientPrefill.gender
                      : null;
                    return `${dateStr} (${age})${genderStr ? ` · ${genderStr}` : ''}`;
                  })()}
                  {(!patientPrefill.birthDate || patientPrefill.birthDate.startsWith('0000')) && patientPrefill.gender && (
                    patientPrefill.gender.toUpperCase() === 'M' ? 'Male'
                      : patientPrefill.gender.toUpperCase() === 'F' ? 'Female'
                      : patientPrefill.gender
                  )}
                </p>
                {(patientPrefill.cellPhone || patientPrefill.email) && (
                  <p className="text-xs text-slate-500">
                    {patientPrefill.cellPhone ? formatPhone(patientPrefill.cellPhone, patientPrefill.cellCountryCode ?? undefined) : ''}
                    {patientPrefill.cellPhone && patientPrefill.email ? ' · ' : ''}
                    {patientPrefill.email ?? ''}
                  </p>
                )}
              </div>
              <PatientAvatar
                patientId={patientPrefill.patientId}
                name={[patientPrefill.firstName, patientPrefill.lastName].filter(Boolean).join(' ')}
                size="sm"
              />
            </button>
          )}
        </div>
      </div>
      )}

      {/* Scrollable middle area. Embedded, the modal body is the only scroller —
          nesting our own overflow-y-auto here would double up. */}
      <form
        id="new-lead-form"
        onSubmit={handleSubmit}
        // One column below `md`. As a row at every width, the `w-80` Patient Match
        // sidebar left the form column 375 − 24 − 320 = 31px on a phone, which is
        // narrower than the padding of the cards inside it — Plan Setup collapsed to
        // an unusable sliver as soon as a similar name matched. `md:` and up is the
        // original row layout untouched.
        className={
          embedded
            ? 'flex flex-col gap-6 md:flex-row'
            : 'flex flex-col gap-6 md:min-h-0 md:flex-1 md:flex-row'
        }
      >
        {/* Left column — form fields, independently scrollable, centered */}
        {/* `flex-1` is a vertical stretch once the parent is a column, and the inner
            scroller has to give way to the wrapper's — both are held to `md:`. */}
        <div
          className={
            embedded
              ? 'min-w-0 p-6 md:flex-1'
              : 'min-w-0 p-6 md:flex-1 md:overflow-y-auto'
          }
        >
          <div className="space-y-6">

      {leadPrefill && (
        <Alert variant="info" className="mb-6" title="Lead prefill applied">
          Intake fields were pre-populated from lead #{leadPrefill.leadId}. Submitting this form will always create a plan and will mark that lead as converted.
        </Alert>
      )}

      {!isAddPlan && selectedPatientId !== null && patientPrefill && (
        <Alert variant="success" className="mb-6" title="Existing patient selected">
          Creating a plan for patient #{patientPrefill.displayId ?? patientPrefill.patientId}. The intake demographics on this page will update that patient before the new plan is created.
          Contact preferences (transactional and marketing email/SMS) are locked to the patient&apos;s existing settings.
        </Alert>
      )}

      {!isAddPlan && selectedPatientId === null && selectedMaster !== null && (
        <Alert variant="info" className="mb-6" title="Shared patient matched">
          This patient already exists at {sharedFromLabel}. The record created here will be linked to that shared
          patient, and its contact preferences (transactional and marketing email/SMS) are locked to the shared
          patient&apos;s settings.
        </Alert>
      )}

      {selectedPatientFailed && (
        <Alert variant="error" className="mb-6">
          <div className="flex flex-col items-start gap-3">
            <span>{PATIENT_DETAILS_FAILED_MESSAGE}</span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void refetchPatientPrefill()}
              loading={patientPrefillFetching}
            >
              {FORM_RETRY_LABEL}
            </Button>
          </div>
        </Alert>
      )}

      {submissionError && (
        <Alert variant="error" className="mb-6" dismissible onDismiss={() => setSubmissionError(null)}>
          {submissionError}
        </Alert>
      )}

      {duplicatePlan && (
        <Alert variant="warning" className="mb-6" title="Duplicate plan type detected">
          This patient already has a {duplicatePlan.planTypeName ?? 'matching'} plan
          {duplicatePlan.name ? ` (${duplicatePlan.name})` : ''}. Enable the duplicate override below if you still want to create another plan of the same type.
        </Alert>
      )}

      {duplicateConflict && (
        <Alert variant="warning" className="mb-6" title="Server-side duplicate check blocked submission">
          Existing plan #{duplicateConflict.planId}
          {duplicateConflict.planTypeName ? ` • ${duplicateConflict.planTypeName}` : ''}
          {duplicateConflict.name ? ` • ${duplicateConflict.name}` : ''}
          {duplicateConflict.date ? ` • ${duplicateConflict.date}` : ''}.
        </Alert>
      )}

        <FormSettingsGate
          status={settingsStatus}
          onRetry={() => void settingsQuery.refetch()}
          retrying={settingsQuery.isFetching}
        >
          <ConfiguredCard card="patient" title="Patient Information" {...cardProps} lead={scanPanel} />
          <ConfiguredCard card="contact" title="Contact Information" {...cardProps} />
          <ConfiguredCard
            card="plan"
            title="Plan Setup"
            {...cardProps}
            footer={duplicatePlan && (
              <Checkbox
                label="Allow duplicate plan type for this patient"
                checked={allowDuplicatePlanType}
                onChange={(event) => setAllowDuplicatePlanType(event.target.checked)}
              />
            )}
          />
          <ConfiguredCard card="interests" title="Interests" {...cardProps} />
          <ConfiguredCard card="notes" title="Notes" {...cardProps} />
        </FormSettingsGate>

          </div>
        </div>

        {/* Right sidebar — Patient Match panel (slides in, independently scrollable) */}
        {!isAddPlan && (
          <div
            // Collapsed, this is `w-0` around a `w-80` child with nothing clipping it,
            // so it projects ~320px of horizontal overflow. That makes the modal body
            // horizontally scrollable (its `overflow-y-auto` computes overflow-x to
            // `auto`), and a stray sideways drag then scrolls the page — which is one
            // of the things that was closing open dropdowns. Clipped below `md` only;
            // on desktop the overflow is invisible and the scroll region harmless.
            className={`shrink-0 transition-all duration-300 ease-in-out ${
              showMatchPanel
                ? 'max-md:w-full md:w-80 opacity-100 translate-x-0'
                : 'w-0 opacity-0 translate-x-4 max-md:overflow-hidden'
            }`}
          >
            {/* Stacked below `md`: full width, left padding restored (it is no longer
                beside the form), and no top padding since the column gap covers it. */}
            <div
              className={
                embedded
                  ? 'p-6 max-md:w-full max-md:pt-0 md:w-80 md:pl-0'
                  : 'p-6 max-md:w-full max-md:pt-0 md:w-80 md:h-full md:overflow-y-auto md:pl-0'
              }
            >
              <div
                className={`bg-white border border-slate-200 rounded-lg p-5 shadow-sm space-y-4${
                  embedded ? ' sticky top-0' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-500" />
                  <h2 className="text-sm font-semibold text-slate-900">Patient Match</h2>
                </div>

                {similarNamesLoading && (
                  <p className="text-sm text-slate-500">Checking for similar names...</p>
                )}

                {!similarNamesLoading && masterMatchesLoading && (
                  <p className="text-sm text-slate-500">Checking shared patient records...</p>
                )}

                {similarNamesError && (
                  <Alert variant="warning" title="Match unavailable">
                    Could not check for existing patients.
                  </Alert>
                )}

                {splitContactMatch && selectedPatientId === null && selectedMaster === null && (
                  <Alert variant="warning" title="Contact details point at two records">
                    {splitNote} Select the shared patient or the account patient below, or correct the
                    contact details. A value already used by a patient in this account has to change
                    before a new patient can be saved.
                  </Alert>
                )}

                {showMasterMatches && (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500">
                      {splitContactMatch
                        ? 'Shared patients:'
                        : `A patient from ${sharedFromLabel} matches this email or phone. Two patients cannot ` +
                          'have the same email or cell phone — select the shared patient to fill the form and link ' +
                          'the new patient to it, or change the email or phone.'}
                    </p>
                    <div className="space-y-2">
                      {masterMatches.map((m) => {
                        const emailMatch = m.matchReasons.includes('email');
                        const phoneMatch = m.matchReasons.includes('phone');
                        return (
                          <div
                            key={m.masterPatientId}
                            className="rounded-lg border border-indigo-200 bg-indigo-50/40 px-3 py-2.5 hover:border-indigo-400 hover:bg-indigo-50 transition-colors cursor-pointer"
                            onClick={() => applyMasterMatch(m)}
                          >
                            <p className="text-sm font-medium text-slate-900">
                              {[m.firstName, m.lastName].filter(Boolean).join(' ').trim() || 'Shared patient'}
                            </p>
                            <p className="text-xs text-indigo-700 mt-0.5">
                              Shared patient from {m.masterAccountName ? `another ${m.masterAccountName} account` : 'another account'}
                            </p>
                            <p className={`text-xs mt-0.5 ${phoneMatch ? 'text-amber-700 font-medium' : 'text-slate-500'}`}>
                              {m.formCellPhone ? formatPhone(m.formCellPhone, m.cellCountryCode ?? undefined) : 'No phone'}
                            </p>
                            {m.email && (
                              <p className={`text-xs truncate ${emailMatch ? 'text-amber-700 font-medium' : 'text-slate-500'}`}>
                                {m.email}
                              </p>
                            )}
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {emailMatch && <span className="inline-block rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-[10px] font-semibold">Email</span>}
                              {phoneMatch && <span className="inline-block rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-[10px] font-semibold">Phone</span>}
                              <span className="inline-block text-[11px] font-medium text-indigo-600 ml-auto">
                                {m.linkedPatientId ? 'Already a patient here — use this patient' : 'Use this shared patient'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!similarNamesLoading && similarNames.length > 0 && selectedPatientId === null && (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500">
                      {splitContactMatch
                        ? 'Patients in this account:'
                        : 'Possible matches found. Select one to avoid creating a duplicate.'}
                    </p>
                    <div className="space-y-2">
                      {similarNames.map((match) => {
                        const reasons = match.matchReasons ?? [];
                        const nameMatch = reasons.includes('name');
                        const lastNameMatch = reasons.includes('lastName');
                        const firstNameMatch = reasons.includes('firstName');
                        const emailMatch = reasons.includes('email');
                        const phoneMatch = reasons.includes('phone');
                        return (
                          <div
                            key={match.patientId}
                            className="rounded-lg border border-slate-200 px-3 py-2.5 hover:border-blue-300 hover:bg-blue-50/50 transition-colors cursor-pointer"
                            onClick={() => setSelectedPatientId(match.patientId)}
                          >
                            <p className={`text-sm font-medium ${nameMatch || lastNameMatch || firstNameMatch ? 'text-amber-700' : 'text-slate-900'}`}>
                              {match.fullName}
                            </p>
                            <p className={`text-xs mt-0.5 ${phoneMatch ? 'text-amber-700 font-medium' : 'text-slate-500'}`}>
                              {match.phone ? formatPhone(match.phone) : 'No phone'}
                            </p>
                            {match.email && (
                              <p className={`text-xs truncate ${emailMatch ? 'text-amber-700 font-medium' : 'text-slate-500'}`}>
                                {match.email}
                              </p>
                            )}
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {nameMatch && <span className="inline-block rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold">Name</span>}
                              {lastNameMatch && !nameMatch && <span className="inline-block rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold">Last Name</span>}
                              {firstNameMatch && !nameMatch && <span className="inline-block rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold">First Name</span>}
                              {emailMatch && <span className="inline-block rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-[10px] font-semibold">Email</span>}
                              {phoneMatch && <span className="inline-block rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-[10px] font-semibold">Phone</span>}
                              <span className="inline-block text-[11px] font-medium text-blue-600 ml-auto">
                                Use this patient
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {selectedMaster !== null && selectedPatientId === null && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2.5">
                      <p className="text-xs font-medium text-indigo-700">Shared patient selected</p>
                      <p className="text-xs text-indigo-600 mt-0.5">
                        The patient created here will be linked to the matching shared patient. Keep the matching
                        email or phone, or the link is skipped.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        setSelectedMaster(null);
                        masterContactRef.current = null;
                      }}
                    >
                      Don&apos;t link &mdash; create standalone
                    </Button>
                  </div>
                )}

                {selectedPatientId !== null && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
                      <p className="text-xs font-medium text-green-700">Existing patient selected</p>
                      <p className="text-xs text-green-600 mt-0.5">
                        Patient #{patientPrefill?.displayId ?? patientPrefill?.patientId ?? selectedPatientId}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        setSelectedPatientId(null);
                        appliedPatientIdRef.current = null;
                      }}
                    >
                      Switch Back to New Patient
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </form>

      {/* Fixed footer. Embedded, it sticks to the bottom of the modal body —
          which only works because the wrappers above drop `overflow-hidden`. */}
      <div
        className={
          embedded
            ? 'sticky bottom-0 z-10 border-t border-slate-200 bg-white px-6 py-3'
            : 'shrink-0 border-t border-slate-200 bg-white px-6 py-3'
        }
      >
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              embedded ? onCancel?.() : navigate(leadId ? `/leads/${leadId}` : '/leads')
            }
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="new-lead-form"
            variant="primary"
            loading={createMutation.isPending || validating}
            disabled={settingsStatus !== 'ready' || !selectedPatientReady}
          >
            {validating ? 'Validating...' : createMutation.isPending
              ? (isAddPlan ? 'Creating Plan...' : 'Creating Patient...')
              : (isAddPlan ? 'Create Plan' : 'Create Patient')}
          </Button>
        </div>
      </div>

      <CameraCaptureModal
        isOpen={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => {
          void handleCapturedFile(file);
        }}
      />

      <Modal
        isOpen={showErrorModal}
        onClose={() => closeErrorModal()}
        title="Please fix these fields"
        size="md"
        footer={
          <Button
            type="button"
            variant="primary"
            onClick={() => closeErrorModal()}
            disabled={modalErrors.length === 0}
          >
            Go to first error
          </Button>
        }
      >
        <p className="text-sm text-slate-600">
          {modalErrors.length === 1
            ? 'The following field needs your attention before this can be submitted:'
            : 'The following fields need your attention before this can be submitted:'}
        </p>
        <ul className="mt-3 space-y-2">
          {modalErrors.map((err) => (
            <li key={err.key}>
              <button
                type="button"
                onClick={() => closeErrorModal(err.anchorId)}
                className="w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-left transition-colors hover:border-red-300 hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
              >
                <span className="block text-sm font-medium text-slate-900">{err.label}</span>
                <span className="block text-xs text-red-600">{err.message}</span>
              </button>
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  );

  // The page-height row below exists only to seat the Add-Plan right panel, and
  // its `h-full` chain has nothing to resolve against inside a modal body.
  if (embedded) return content;

  return (
    <div className="flex h-full min-h-0 overflow-hidden">
      {content}

      {/* Right panel — full-height patient details sidebar (Add Plan mode) */}
      {isAddPlan && initialPatientId && (
        <DetailRightPanel defaultCollapsed toggleTrigger={expandPatientPanel}>
          <DetailProfile detail={patientDetail} loading={patientDetailLoading} />
        </DetailRightPanel>
      )}
    </div>
  );
}
